import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {first, take} from 'rxjs/operators';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {ListResponse} from '../interface/response/list-response';
import {HttpClient} from '@angular/common/http';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {Meeting} from '../interface/meeting';
import {OfwcaMeeting} from '../interface/ofwca-meeting';
import {Uuid} from '../interface/response/uuid';
import {Ofwca} from "../interface/ofwca";

@Injectable({
  providedIn: 'root'
})
export class MeetingService {
  public readonly meetingListSubject: ReplaySubject<CountedAndPaginated<Meeting>>
    = new ReplaySubject<CountedAndPaginated<Meeting>>(1);
  public readonly ofwcaMeetingListSubject: ReplaySubject<CountedAndPaginated<OfwcaMeeting>>
    = new ReplaySubject<CountedAndPaginated<OfwcaMeeting>>(1);
  public readonly meetingNotCountedListSubject: ReplaySubject<ListResponse<Meeting>>
    = new ReplaySubject<ListResponse<Meeting>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  sendForm(formData: object, meetingId: string): Observable<any> {
    if (!meetingId) {
      return this.httpClient.post(`/rest/crm/meeting`, formData)
        .pipe(take(1));
    } else {
      return this.httpClient.put(`/rest/crm/meeting/${meetingId}`, formData)
        .pipe(take(1));
    }
  }

  get(meetingId: string): Observable<Meeting> {
    return this.httpClient.get(`/rest/crm/meeting/${meetingId}`)
      .pipe(first()) as Observable<Meeting>;
  }

  delete(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/meeting/${uuid}`)
      .pipe(first());
  }

  public loadList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/meeting?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<Meeting>) => {
        this.meetingListSubject.next(result);
      });
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object, observable?: ReplaySubject<ListResponse<Meeting>>): void {
    this.httpClient
      .get(`/rest/crm/meeting?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<Meeting>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.meetingNotCountedListSubject.next(result);
        }
      });
  }

  downloadReport(filters: any): void {
    this.document.location.href = `/rest/crm/meeting/method/report?` + this.filterService.parseFilters(filters);
  }

  registerToMeeting(meetingId: string, ofwcaIds: string[]): Observable<any> {
    return this.httpClient.post(`/rest/crm/meeting/${meetingId}/registerOfwcas`, {
      ofwcas: ofwcaIds
    }).pipe(first());
  }

  getRegisteredOfwcas(meetingId: string, sort?: { sortBy?: string; sortOrder?: string }): Observable<ListResponse<OfwcaMeeting>> {
    if (sort !== null){
      return this.httpClient.get(`/rest/crm/meeting/${meetingId}/ofwca?&sortBy=${sort.sortBy}&sortOrder=${sort.sortOrder}&`)
        .pipe(first()) as Observable<ListResponse<OfwcaMeeting>>;
    } else {
      return this.httpClient.get(`/rest/crm/meeting/${meetingId}/ofwca`)
        .pipe(first()) as Observable<ListResponse<OfwcaMeeting>>;
    }
  }

  deregisterOfwca(ofwcaMeetingId: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/ofwcaMeeting/${ofwcaMeetingId}`)
      .pipe(first()) as Observable<any>;
  }

  updateOfwcaMeeting(ofwcaMeetingId: string, formData: any): Observable<any> {
    return this.httpClient.put(`/rest/crm/ofwcaMeeting/${ofwcaMeetingId}`, formData)
      .pipe(first());
  }

  getOfwcaMeetings(ofwcaId: string): Observable<ListResponse<OfwcaMeeting>> {
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}/meeting`)
      .pipe(first()) as Observable<ListResponse<OfwcaMeeting>>;
  }

  importData(meetingId: string, fileContent: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/meeting/${meetingId}/import`, {
      fileContent
    });
  }

  downloadMeetingReport(uuid: string , sort?: { sortOrder?: string}):  Observable<Uuid> {
    return this.httpClient.post( `/rest/crm/meeting/${uuid}/method/report?sortOrder=${sort.sortOrder ?? ''}&`, {})
      .pipe(first()) as Observable<Uuid>;
  }

  generateExamDocuments(meetingId: string, ofwcaIds: string[], updateRegistrationStatus: boolean): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/meeting/${meetingId}/generateExamDocuments`, {
      ofwcas: ofwcaIds,
      updateRegistrationStatus
    }).pipe(first()) as Observable<Uuid>;
  }

  downloadCertificate(ofwcaMeetingId: string): void {
    this.document.location.href = `/rest/crm/ofwcaMeeting/${ofwcaMeetingId}/certificate`;
  }

  sendCertificate(ofwcaMeetingId: string): Observable<any> {
    return this.httpClient.get(`/rest/crm/ofwcaMeeting/${ofwcaMeetingId}/sendCertificate`)
      .pipe(first());
  }
  sendCertificates(meetingId: string): Observable<any> {
    return this.httpClient.get( `/rest/crm/ofwcaMeeting/${meetingId}/sendCertificates`)
      .pipe(first());
  }

  downloadCertificates(meetingId: string): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/meeting/${meetingId}/certificates`, {})
      .pipe(first()) as Observable<Uuid>;
  }
  getOfwcaMeetingsList(page: number, pageSize: number, filters: object):  void {
    this.httpClient.get(`/rest/crm/ofwca-meeting?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<OfwcaMeeting>) => {
        this.ofwcaMeetingListSubject.next(result);
      });
  }
  public getOfwcaMeetingListSubject(): ReplaySubject<CountedAndPaginated<OfwcaMeeting>> {
    return this.ofwcaMeetingListSubject;
  }
  downloadOfwcaMeetingReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwca-meeting/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }
  public downloadFile(uuid: string): void {
    this.document.location.href = `/rest/crm/ofwcaMeeting/${uuid}/downloadFile`;
  }
}
